<template>
  <a-modal
    :title="title"
    width="800px"
    :open="true"
    :maskClosable="false"
    :confirmLoading="loading"
    centered
    :footer="null"
    @cancel="
      () => {
        // resetFields();
        $emit('cancel');
      }
    "
  >
    <a-upload
      :multiple="false"
      v-model:file-list="fileList"
      @remove="handleRemove"
      :customRequest="customRequestFun"
    >
      <a-button
        type="primary"
        v-if="fileList.length == 0"
      >
        <plus-outlined />
        选择文件
      </a-button>
    </a-upload>
  </a-modal>
</template>

<script lang="ts">
import { PlusOutlined } from '@ant-design/icons-vue';
import { defineComponent, ref } from 'vue';
import { message } from 'ant-design-vue';
import { read, utils } from 'xlsx';
import { getApiData } from 'liyu-pc-base/api/api-model';
import dayjs from 'dayjs';
import { parseExcelDate } from 'liyu-pc-base/utils/dateUtil';

interface FileItem {
  uid: string;
  fid?: number;
  name?: string;
  status?: string;
  response?: Response;
  percent?: number;
  url: string;
  preview?: string;
  originFileObj?: any;
}

export default defineComponent({
  name: 'ImportExcelModal',
  components: { PlusOutlined },
  props: {
    title: {
      type: String,
      required: true,
    },
    configList: {
      type: Array,
      required: true,
      default: () => [],
    },
  },
  emits: ['ok', 'cancel'],
  setup(props, { emit }) {
    const fileList = ref<FileItem[]>([]);
    const loading = ref(false);
    const normalizeExcelHeader = (header: any) => {
      return header ? String(header).trim().replace(/\s*\[必填\]\s*$/, '') : '';
    };

    const customRequest = async (options: any) => {
      const { file, onSuccess, onError } = options;
      loading.value = true;
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];

          // 获取表头以确定列索引
          const sheetData: any[][] = utils.sheet_to_json(worksheet, { header: 1 });
          const headers = (sheetData[0] || []).map(h => normalizeExcelHeader(h));
          const jsonData: any[] = utils.sheet_to_json(worksheet);
          const normalizedJsonData = jsonData.map((row: any) => {
            const normalizedRow: any = {};
            Object.keys(row).forEach(key => {
              normalizedRow[normalizeExcelHeader(key)] = row[key];
            });
            return normalizedRow;
          });

          console.log('Excel Headers:', headers);
          console.log('Excel JSON Data:', normalizedJsonData);

          const apiCache: any = {};
          const remoteSelectCols = props.configList.filter(
            (col: any) => col.type === 'remoteselectpro' && col.postUrl,
          );
          if (remoteSelectCols.length > 0) {
            for (const col of remoteSelectCols as any[]) {
              const targetLabel = col.label ? String(col.label).trim() : '';
              const fieldLabels = normalizedJsonData
                .map((row: any) => {
                  return row[targetLabel] != null ? String(row[targetLabel]).trim() : null;
                })
                .filter(v => v !== null && v !== '');
              const uniqueLabels = [...new Set(fieldLabels)];

              if (uniqueLabels.length > 0) {
                apiCache[col.name] = {};
                const lKey = col.labelKey || 'name';
                const vKey = col.valueKey || 'id';

                await Promise.all(
                  uniqueLabels.map(async label => {
                    const getLabel = (opt: any) => {
                      if (
                        col.labelKeyList &&
                        col.labelKeyList.length > 0 &&
                        col.labelKeyType !== 1
                      ) {
                        return col.labelKeyList
                          .map(k => (opt[k] !== undefined && opt[k] !== null ? String(opt[k]) : ''))
                          .join(col.labelSpace || '-');
                      }
                      return opt[lKey] !== undefined && opt[lKey] !== null ? String(opt[lKey]) : '';
                    };
                    if (col.options && col.options.find(opt => getLabel(opt) === label)) {
                      return;
                    }

                    try {
                      const conditions = [];
                      if (col.labelKeyList && col.labelKeyList.length > 0 && col.labelSpace) {
                        const lableValue = label.split(col.labelSpace);
                        if (lableValue.length === col.labelKeyList.length) {
                          const conditionItem = [];
                          col.labelKeyList.forEach((key: any, index: any) => {
                            conditionItem.push({
                              field_name: key,
                              operator: 'eq',
                              value: [lableValue[index]],
                              format: 'choices',
                              class_name: 'str',
                            });
                          });
                          conditions.push(conditionItem);
                        }
                      } else {
                        conditions.push([
                          {
                            field_name: lKey,
                            operator: 'eq',
                            value: [label],
                            format: 'choices',
                            class_name: 'str',
                          },
                        ]);
                      }
                      const params: any = {
                        current: 1,
                        pageSize: 50,
                        is_pagination: true,
                        conditions: conditions,
                      };
                      const res = await getApiData(params, col.postUrl, false, {
                        skip_error: true,
                      });
                      if (res && res.data && res.data.length > 0) {
                        const matched = res.data.find(opt => getLabel(opt) === label);
                        if (matched) {
                          apiCache[col.name][label] = matched;
                        }
                      }
                    } catch (err) {
                      console.error('Fetch remote select error', err);
                    }
                  }),
                );
              }
            }
          }

          if (normalizedJsonData.length === 0) {
            message.info('Excel文件中未检测到数据行（除表头外），请检查文件');
            loading.value = false;
            if (onSuccess) onSuccess();
            return;
          }

          let matchCount = 0;
          const result = normalizedJsonData.map((row: any, rowIndex: number) => {
            const mappedRow: any = {};
            props.configList.forEach((col: any) => {
              const targetLabel = col.label ? String(col.label).trim() : '';
              let excelValue = row[targetLabel];
              if (excelValue !== undefined && excelValue !== null) {
                excelValue = String(excelValue).trim();
              }
              const name = col.name;

              if (excelValue !== undefined && excelValue !== null && excelValue !== '')
                matchCount++;

              // 查找列索引用于 excel_position
              const colIndex = headers.indexOf(targetLabel);
              const excel_position =
                colIndex !== -1 ? `${utils.encode_col(colIndex)}${rowIndex + 2}` : '';

              let value = excelValue;
              let raw_value = excelValue;
              let error = null;

              const selectTypes = [
                'select',
                'radio',
                'checkbox',
                'treeselect',
                'cascader',
                'remoteselectpro',
              ];
              if (selectTypes.includes(col.type)) {
                const vKey = col.valueKey || (col.type === 'remoteselectpro' ? 'id' : 'value');
                const lKey = col.labelKey || (col.type === 'remoteselectpro' ? 'name' : 'label');
                const getLabel = (opt: any) => {
                  if (col.labelKeyList && col.labelKeyList.length > 0 && col.labelKeyType !== 1) {
                    return col.labelKeyList
                      .map(k => (opt[k] !== undefined && opt[k] !== null ? String(opt[k]) : ''))
                      .join(col.labelSpace || '-');
                  }
                  return opt[lKey] !== undefined && opt[lKey] !== null ? String(opt[lKey]) : '';
                };
                let option = null;
                if (col.options && col.options.length > 0) {
                  option = col.options.find((opt: any) => getLabel(opt) === excelValue);
                }
                if (!option && col.type === 'remoteselectpro' && apiCache[col.name]) {
                  option = apiCache[col.name][excelValue];
                }

                if (option) {
                  value = option[vKey];
                  raw_value = getLabel(option);
                } else if (excelValue !== undefined && excelValue !== null && excelValue !== '') {
                  let valueOption = null;
                  if (col.options && col.options.length > 0) {
                    valueOption = col.options.find((opt: any) => String(opt[vKey]) === excelValue);
                  }
                  if (valueOption) {
                    value = valueOption[vKey];
                    raw_value = getLabel(valueOption);
                  } else {
                    error = `选项 "${excelValue}" 不存在`;
                  }
                }
              }
              if (['number', 'money', 'volume', 'weight'].includes(col.type)) {
                try {
                  value = Number(value);
                  if (!Number.isFinite(value) || Number.isNaN(value)) {
                    error = `值 "${excelValue}" 不是数字`;
                  }
                } catch (e) {
                  error = `值 "${excelValue}" 不是数字`;
                }
              }
              if (['timepicker'].includes(col.type)) {
                const result = parseExcelDate(excelValue, 'timepicker', col.format);
                if (result.isValid) {
                  value = result.value;
                } else {
                  error = result.error || `值 "${excelValue}" 不是时间`;
                }
              }
              if (['datepicker'].includes(col.type)) {
                const result = parseExcelDate(excelValue, 'datepicker', col.format);
                if (result.isValid) {
                  value = result.value;
                } else {
                  error = result.error || `值 "${excelValue}" 不是日期`;
                }
              }
              mappedRow[name] = {
                value: value === undefined ? null : value,
                raw_value: raw_value === undefined ? null : raw_value,
                excel_position: excel_position,
                error: error,
              };
            });
            return mappedRow;
          });

          if (matchCount === 0) {
            message.warning('Excel表头与配置不匹配，请检查表头名称是否正确');
            if (onError) onError();
            return;
          }

          if (result.length > 0) {
            emit('ok', result);
            if (onSuccess) onSuccess();
          } else {
            message.info('未识别到Excel文件中数据，请确认文件是否正确');
            if (onError) onError();
          }
        } catch (err) {
          console.error(err);
          message.error('解析Excel失败');
          try {
            if (onError) onError();
          } catch (e) {}
        } finally {
          loading.value = false;
        }
      };
      reader.onerror = () => {
        loading.value = false;
        message.error('文件读取失败');
        try {
          if (onError) onError();
        } catch (e) {}
      };
      reader.readAsArrayBuffer(file);
    };

    const customRequestFun = (options: any) => {
      customRequest(options);
      return false;
    };

    const handleRemove = (file: any) => {
      const index = fileList.value.indexOf(file);
      fileList.value.splice(index, 1);
    };

    return {
      props,
      loading,
      handleRemove,
      customRequest,
      customRequestFun,
      fileList,
    };
  },
});
</script>
